import React, { createContext, useState, useEffect, useContext } from 'react';
import apiClient from '../services/apiClient';
import { AUTH_ENDPOINTS } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is already authenticated on initial load
    const checkAuthStatus = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          setLoading(false);
          return;
        }

        // Validate token by fetching current user
        const userData = await apiClient.get(AUTH_ENDPOINTS.CURRENT_USER);
        setUser(userData);
      } catch (err) {
        console.error('Authentication error:', err);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setError('Authentication failed. Please login again.');
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post(AUTH_ENDPOINTS.LOGIN, { email, password }, true);
      
      // Store tokens
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      
      // Fetch user data after login
      const userData = await apiClient.get(AUTH_ENDPOINTS.CURRENT_USER);
      setUser(userData);
      
      return true;
    } catch (err) {
      console.error('Login failed:', err);
      
      // Set appropriate error message
      const errorMessage = err.response?.data?.message || 
                          'Login failed. Please check your credentials and try again.';
      setError(errorMessage);
      
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  const register = async ({ name, email, password, platformType="TASK_TRACKING" }) => {
    setLoading(true);
    setError(null);
    try {
      await apiClient.post(AUTH_ENDPOINTS.REGISTER, {
        name,
        email,
        password,
        platform_type: platformType
      }, true);
      
      return true;
    } catch (err) {
      console.error('Registration failed:', err);
      
      // Set appropriate error message
      const errorMessage = err.response?.data?.message || 
                           'Registration failed. Please try again.';
      setError(errorMessage);
      
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      // Call logout endpoint if you have one
      try {
        await apiClient.post(AUTH_ENDPOINTS.LOGOUT);
      } catch (err) {
        console.warn('Logout API call failed, proceeding with local logout', err);
      }
      
      // Clear local storage and state
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
    } catch (err) {
      console.error('Logout error:', err);
      setError('Logout failed');
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext; 